import React from 'react';
import PropTypes from 'prop-types';

/**
 * EnhancedQuestionDisplay Component
 * Displays math questions with optional visual diagrams
 */
const EnhancedQuestionDisplay = ({ 
  question, 
  question_text,
  options,
  selectedAnswer,
  onAnswerSelect,
  explanation,
  hint,
  has_diagram = false,
  diagram_svg,
  isTestMode = false,
  questionNumber, 
  showCorrectAnswer = false,
  className = '' 
}) => {
  // Handle both question object and individual props
  const questionData = question || {
    question_text,
    options: options || [],
    explanation,
    hint,
    has_diagram,
    diagram_svg
  };

  const {
    id,
    question_text: qText,
    options: qOptions = [],
    correct_index,
    explanation: qExplanation,
    has_diagram: qHasDiagram = false,
    diagram_type,
    diagram_data,
    diagram_svg: qDiagramSvg,
    topic,
    difficulty
  } = questionData;

  // Use the direct props or the question object values
  const finalQuestionText = question_text || qText;
  const finalOptions = options || qOptions;
  const finalExplanation = explanation || qExplanation;
  const finalHasDiagram = has_diagram || qHasDiagram;
  const finalDiagramSvg = diagram_svg || qDiagramSvg;

  const handleOptionClick = (optionIndex) => {
    if (onAnswerSelect && !showCorrectAnswer) {
      onAnswerSelect(optionIndex);
    }
  };

  const getOptionStyle = (optionIndex) => {
    const baseStyle = "w-full text-left p-4 border-2 rounded-lg transition-all duration-200 hover:shadow-md";

    if (showCorrectAnswer) {
      if (optionIndex === correct_index) {
        return `${baseStyle} border-green-500 bg-green-50 text-green-800`;
      } else if (optionIndex === selectedAnswer && optionIndex !== correct_index) {
        return `${baseStyle} border-red-500 bg-red-50 text-red-800`;
      } else {
        return `${baseStyle} border-gray-300 bg-gray-50 text-gray-600`;
      }
    } else {
      if (selectedAnswer === optionIndex) {
        return `${baseStyle} bg-white`; // Will apply inline style for color
      } else {
        return `${baseStyle} border-gray-300 bg-white`;
      }
    }
  };

  const getOptionInlineStyle = (optionIndex) => {
    if (!showCorrectAnswer && selectedAnswer === optionIndex) {
      return {
        borderColor: 'var(--mc-primary)',
        background: 'var(--mc-primary-lighter)',
        color: 'var(--mc-primary)'
      };
    }
    return {};
  };

  return (
    <div className={`enhanced-question-display ${className}`} style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Question Header - Centered and Prominent */}
      <div className="question-header mb-8" style={{ textAlign: 'center' }}>
        <div className="flex items-center justify-center mb-4">
          <div className="question-meta">
            <span className="question-number text-2xl font-bold" style={{ color: 'var(--mc-primary)' }}>
              Question {questionNumber}
            </span>
            <div className="meta-tags mt-2 space-x-2" style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
              {topic && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ background: 'var(--mc-primary-lighter)', color: 'var(--mc-primary)' }}>
                  {topic}
                </span>
              )}
              {difficulty && (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                  difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {difficulty}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Question Text - Prominent and Centered */}
        <div className="question-text" style={{
          padding: '2rem',
          background: 'var(--mc-primary-lighter)',
          borderRadius: 'var(--radius-lg)',
          border: '2px solid var(--mc-primary-light)',
          marginBottom: '2rem'
        }}>
          <h2 className="text-2xl font-semibold leading-relaxed" style={{ color: 'var(--mc-charcoal)' }}>
            {finalQuestionText}
          </h2>
        </div>
      </div>

      <div className="question-content">
        {/* Options Panel - Centered */}
        <div className="options-panel" style={{ maxWidth: '700px', margin: '0 auto' }}>
          <div className="options-section">
            <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--mc-gray-600)', textAlign: 'center' }}>Choose your answer:</h3>

            <div className="space-y-3">
              {finalOptions.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleOptionClick(index)}
                  className={getOptionStyle(index)}
                  style={getOptionInlineStyle(index)}
                  disabled={showCorrectAnswer}
                >
                  <div className="flex items-center">
                    <span className="option-label mr-3 font-semibold text-lg">
                      {String.fromCharCode(65 + index)}.
                    </span>
                    <span className="option-text text-base">
                      {option}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Explanation Panel (shown when answer is revealed) */}
      {showCorrectAnswer && finalExplanation && (
        <div className="explanation-panel mt-8" style={{ maxWidth: '700px', margin: '2rem auto 0' }}>
          <div className="explanation-section rounded-lg p-6" style={{ background: 'var(--mc-primary-lighter)', border: '1px solid var(--mc-primary-light)' }}>
            <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--mc-primary)' }}>
              <svg className="w-5 h-5 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Explanation
            </h3>
            <p className="leading-relaxed" style={{ color: 'var(--mc-charcoal)' }}>
              {finalExplanation}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

EnhancedQuestionDisplay.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.string.isRequired,
    question_text: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
    correct_index: PropTypes.number.isRequired,
    explanation: PropTypes.string,
    has_diagram: PropTypes.bool,
    diagram_type: PropTypes.string,
    diagram_data: PropTypes.object,
    diagram_svg: PropTypes.string,
    topic: PropTypes.string,
    difficulty: PropTypes.string
  }).isRequired,
  questionNumber: PropTypes.number.isRequired,
  selectedAnswer: PropTypes.number,
  onAnswerSelect: PropTypes.func,
  showCorrectAnswer: PropTypes.bool,
  className: PropTypes.string
};

export default EnhancedQuestionDisplay;